import type { Permission } from '@/types'

/**
 * Permission catalogue. Format: `<resource>:<action>`.
 * `*` acts as a wildcard within a resource or globally (`*:*`).
 */
export const RESOURCES = [
  'dashboard',
  'stakeholders',
  'businesses',
  'skills',
  'organizations',
  'departments',
  'political',
  'opportunities',
  'projects',
  'grievances',
  'meetings',
  'calendar',
  'tasks',
  'documents',
  'reports',
  'maps',
  'search',
  'settings',
  'users',
  'audit',
  'billing',
  'cleanup',
] as const

export const ACTIONS = ['view', 'create', 'edit', 'delete', 'assign', 'approve', 'export'] as const

/**
 * The one role that can never be renamed, deleted, or edited away from full
 * access (Settings -> Roles & Permissions enforces this both client- and
 * server-side). Every other role -- including the 7 seed names below -- is
 * an ordinary, admin-editable row.
 */
export const LOCKED_ROLE = 'ADMIN'

/** Default labels for the seed roles. Unknown/custom roles fall back to the raw role string — use `ROLE_LABELS[role] ?? role`. */
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  MINISTER: 'Minister',
  CHIEF_SECRETARY: 'Chief Secretary',
  OFFICE_STAFF: 'Office Staff',
  PERSONAL_ASSISTANT: 'Personal Assistant',
  DEPARTMENT_OFFICER: 'Department Officer',
  DISTRICT_COORDINATOR: 'District Coordinator',
  VIEWER: 'Viewer',
}

const ALL: Permission[] = ['*:*']

// "billing" (AWS cost data) and "cleanup" (which records are old/heavy
// enough to consider deleting) are deliberately excluded from the default
// "view everything" set below -- unlike every other resource here, no seed
// role other than ADMIN (which gets '*:*') should see them out of the box.
// An admin can still grant either to another role explicitly in Settings ->
// Roles & Permissions, since both stay in RESOURCES for that matrix.
const viewAll: Permission[] = RESOURCES.filter((r) => r !== 'billing' && r !== 'cleanup').map((r) => `${r}:view`)

/** Default effective permissions per seed role. Editable at runtime in Settings; a custom role has no entry here — its permissions live only in the backend `role_permissions` table. */
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  ADMIN: ALL,
  MINISTER: viewAll,
  CHIEF_SECRETARY: [
    ...viewAll,
    'stakeholders:*',
    'opportunities:*',
    'grievances:*',
    'meetings:*',
    'tasks:*',
    'projects:*',
    'reports:*',
    'documents:*',
    'political:*',
    'settings:view',
    'users:view',
  ],
  PERSONAL_ASSISTANT: [
    ...viewAll,
    'meetings:*',
    'calendar:*',
    'tasks:*',
    'stakeholders:edit',
    'stakeholders:create',
    'documents:create',
    'documents:edit',
  ],
  OFFICE_STAFF: [
    ...viewAll,
    'stakeholders:create',
    'stakeholders:edit',
    'grievances:create',
    'grievances:edit',
    'grievances:assign',
    'tasks:create',
    'tasks:edit',
    'documents:create',
    'documents:edit',
    'businesses:edit',
    'skills:edit',
    'political:view',
    'political:edit',
    'political:create',
  ],
  DEPARTMENT_OFFICER: [
    'dashboard:view',
    'grievances:view',
    'grievances:edit',
    'grievances:assign',
    'tasks:view',
    'tasks:edit',
    'meetings:view',
    'documents:view',
    'reports:view',
    'opportunities:view',
    'projects:view',
    'departments:view',
    'departments:edit',
    'political:view',
  ],
  DISTRICT_COORDINATOR: [
    'dashboard:view',
    'stakeholders:view',
    'stakeholders:create',
    'stakeholders:edit',
    'businesses:view',
    'skills:view',
    'departments:view',
    'political:view',
    'grievances:view',
    'grievances:create',
    'meetings:view',
    'maps:view',
    'tasks:view',
    'reports:view',
  ],
  VIEWER: viewAll,
}

/** Wildcard-aware permission check. */
export function hasPermission(perms: Permission[], required: Permission): boolean {
  if (!required) return true
  if (perms.includes('*:*')) return true
  if (perms.includes(required)) return true
  const [res, action] = required.split(':')
  return perms.includes(`${res}:*`) || perms.includes(`*:${action}`)
}
